import * as ast from "../m-script/mscript-ast.js";
import * as lang from "../m-script/mscript-lang.js";

class QLangParseError extends Error {

    constructor(message, position, source) {
        super(message);
        this.name = 'QLangParseError';
        this.position = position;
        this.source = source;
    }

}

export class QLangParser {

    static create() {
        return new QLangParser();
    }

    static parseFilter(input) {
        return new QLangParser().parseFilter(input);
    }

    static parseOrder(input) {
        return new QLangParser().parseOrder(input);
    }

    static parseOffset(input) {
        return new QLangParser().parseOffset(input);
    }

    static parseLimit(input) {
        return new QLangParser().parseLimit(input);
    }

    constructor() {
        this.errors = [];
        this.reset('');
    }

    getErrors() {
        return this.errors;
    }

    parse(input) {

        this.errors = [];

        try {
            const queryString = (input || '').replace(/^\?/, '');
            if (!queryString)
                return ast.query();

            const params = new URLSearchParams(queryString);
            const filter = this.parseFilter(params.get('$filter'));
            if (filter === false)
                return false;

            const order = this.parseOrder(params.get('$order'));
            if (order === false)
                return false;

            const offset = this.parseOffset(params.get('$offset'));
            if (offset === false)
                return false;

            const limit = this.parseLimit(params.get('$limit'));
            if (limit === false)
                return false;

            return ast.query(filter, order, limit, offset);

        } catch (error) {
            this.errors = [error];
            return false;
        }

    }

    parseFilter(input) {

        if (input === null || input === undefined || input === '')
            return null;

        try {
            this.reset(input);
            const node = this.parseFilterExpression();
            this.skipWs();
            if (!this.eof())
                throw this.err("Unexpected trailing input");
            return node;
        } catch (error) {
            this.errors = [error];
            return false;
        }

    }

    parseOrder(input) {

        if (input === null || input === undefined || input === '')
            return null;

        try {
            this.reset(input);
            const items = this.parseOrderExpression();
            this.skipWs();
            if (!this.eof())
                throw this.err("Unexpected trailing input in order");
            return items;
        } catch (error) {
            this.errors = [error];
            return false;
        }

    }

    parseOrderBy(input) {
        return this.parseOrder(input);
    }

    parseOffset(input) {

        if (input === null || input === undefined || input === '')
            return null;

        try {
            this.reset(input);
            const value = this.parseUIntLiteral();
            this.skipWs();
            if (!this.eof())
                throw this.err("Unexpected trailing input in offset");
            return value;
        } catch (error) {
            this.errors = [error];
            return false;
        }

    }

    parseLimit(input) {

        if (input === null || input === undefined || input === '')
            return null;

        try {
            this.reset(input);
            const value = this.parseUIntLiteral();
            this.skipWs();
            if (!this.eof())
                throw this.err("Unexpected trailing input in limit");
            return value;
        } catch (error) {
            this.errors = [error];
            return false;
        }

    }

    reset(input) {
        this.s = input || '';
        this.i = 0;
        this.n = this.s.length;
    }

    parseFilterExpression() {

        this.skipWs();

        if (this.peek() === "'")
            return this.parseStringLiteral();

        if (this.peek() === '!')
            return this.parseBangLiteral();

        if (this.peekIsNumberStart())
            return this.parseNumberLiteral();

        if (this.peekIsIdentStart()) {

            const name = this.parseIdentifierText();
            this.skipWs();

            if (this.peek() === '(') {
                this.consume('(');
                let args = [];
                this.skipWs();
                if (this.peek() !== ')')
                    args = this.parseArgList();
                this.skipWs();
                this.consume(')');

                if (lang.ALL_UN_OPS.includes(name))
                    return ast.op(ast.NODE_UN_OP, name, args);
                if (lang.ALL_BIN_OPS.includes(name))
                    return ast.op(ast.NODE_BIN_OP, name, args);
                if (lang.ALL_FUNCS.includes(name))
                    return ast.funccall(name, args);
            }

            if (name === 'true')
                return ast.bool(true);
            if (name === 'false')
                return ast.bool(false);
            if (name === 'null')
                return ast.nullLiteral();

            return ast.ident(name);
        }

        throw this.err("Expected expression");
    }

    parseArgList() {

        const args = [];
        args.push(this.parseFilterExpression());

        while (true) {
            this.skipWs();
            if (this.peek() !== ',')
                break;
            this.consume(',');
            args.push(this.parseFilterExpression());
        }

        return args;

    }

    parseOrderExpression() {

        this.skipWs();
        if (!this.peekIsIdentStart())
            throw this.err("Expected identifier in order");

        const items = [];
        items.push(this.parseOrderItem());

        while (true) {
            this.skipWs();
            if (this.peek() !== ',')
                break;
            this.consume(',');
            items.push(this.parseOrderItem());
        }

        return items;

    }

    parseOrderItem() {

        this.skipWs();
        const field = this.parseIdentifierText();
        this.skipWs();

        let desc = false;
        if (this.peek() === '-') {
            this.consume('-');
            desc = true;
        }

        return ast.order(field, desc);

    }

    parseNumberLiteral() {

        this.skipWs();

        const start = this.i;

        if (this.peek() === '-')
            this.i++;

        const digits = this.readWhile(c => c >= '0' && c <= '9');
        if (digits === '')
            throw this.err("Invalid number literal");

        let frac = '';
        if (this.peek() === '.') {
            this.i++;
            const fracDigits = this.readWhile(c => c >= '0' && c <= '9');
            if (fracDigits === '')
                throw this.err("Invalid number literal (missing fraction digits)");
            frac = `.${fracDigits}`;
        }

        const text = this.s.substring(start, this.i);
        if (!frac)
            return ast.intnum(parseInt(text, 10));

        return ast.floatnum(parseFloat(text));

    }

    parseUIntLiteral() {

        this.skipWs();
        const start = this.i;

        const digits = this.readWhile(c => c >= '0' && c <= '9');
        if (digits === '')
            throw this.err("Expected integer");

        const value = parseInt(this.s.substring(start, this.i), 10);
        if (value < 0)
            throw this.err("Integer out of range");

        return value;

    }

    parseStringLiteral() {

        this.skipWs();
        this.consume("'");

        let out = '';

        while (!this.eof()) {
            const c = this.peek();

            if (c === "'") {
                this.consume("'");
                return ast.string(out);
            }

            if (c === '!') {
                this.consume('!');
                const next = this.peek();
                if (next === "'" || next === '!') {
                    out += next;
                    this.i++;
                    continue;
                }
                throw this.err("Invalid string escape after '!': expected ! or '");
            }

            out += c;
            this.i++;
        }

        throw this.err("Unterminated string literal");

    }

    parseBangLiteral() {

        this.skipWs();
        this.consume('!');

        if (this.eof())
            throw this.err("Dangling '!'");

        if (this.looksLikeDatePrefixAfterBang() || this.looksLikeTimePrefixAfterBang())
            return this.parseDateTimeLiteralAfterBang();

        const symbol = this.parseSymbolText();
        return ast.symbol(symbol);

    }

    looksLikeDatePrefixAfterBang() {

        if (this.i + 10 > this.n)
            return false;

        const isDigit = c => c !== null && c >= '0' && c <= '9';

        const y0 = this.s[this.i + 0] ?? null;
        const y1 = this.s[this.i + 1] ?? null;
        const y2 = this.s[this.i + 2] ?? null;
        const y3 = this.s[this.i + 3] ?? null;
        const d4 = this.s[this.i + 4] ?? null;
        const m0 = this.s[this.i + 5] ?? null;
        const m1 = this.s[this.i + 6] ?? null;
        const d7 = this.s[this.i + 7] ?? null;
        const d0 = this.s[this.i + 8] ?? null;
        const d1 = this.s[this.i + 9] ?? null;

        return isDigit(y0) && isDigit(y1) && isDigit(y2) && isDigit(y3)
            && d4 === '-'
            && isDigit(m0) && isDigit(m1)
            && d7 === '-'
            && isDigit(d0) && isDigit(d1);

    }

    looksLikeTimePrefixAfterBang() {

        if (this.i + 8 > this.n)
            return false;

        const isDigit = c => c !== null && c >= '0' && c <= '9';

        const h0 = this.s[this.i + 0] ?? null;
        const h1 = this.s[this.i + 1] ?? null;
        const d2 = this.s[this.i + 2] ?? null;
        const m0 = this.s[this.i + 3] ?? null;
        const m1 = this.s[this.i + 4] ?? null;
        const d5 = this.s[this.i + 5] ?? null;
        const s0 = this.s[this.i + 6] ?? null;
        const s1 = this.s[this.i + 7] ?? null;

        return isDigit(h0) && isDigit(h1)
            && d2 === '.'
            && isDigit(m0) && isDigit(m1)
            && d5 === '.'
            && isDigit(s0) && isDigit(s1);

    }

    parseDateTimeLiteralAfterBang() {

        let date = null;
        let time = null;
        let zone = null;

        if (this.looksLikeDatePrefixAfterBang()) {
            const year = this.readExactDigits(4, "year");
            this.consume('-');
            const month = this.readExactDigits(2, "month");
            this.consume('-');
            const day = this.readExactDigits(2, "day");

            date = `${year}-${month}-${day}`;

            if (this.peek() === 'T') {
                this.consume('T');
                [time, zone] = this.parseTimeAndOptionalZone();
            }

            return ast.datetime(date, time, zone);
        }

        if (this.looksLikeTimePrefixAfterBang()) {
            [time, zone] = this.parseTimeAndOptionalZone(false);
            return ast.datetime(null, time, zone);
        }

        throw this.err("Invalid datetime literal after '!'");

    }

    parseTimeAndOptionalZone(allowZone = true) {

        const hh = this.readExactDigits(2, "hour");
        this.consume('.');
        const mm = this.readExactDigits(2, "minute");
        this.consume('.');
        const ss = this.readExactDigits(2, "second");

        const time = `${hh}:${mm}:${ss}`;
        let zone = null;

        if (!allowZone)
            return [time, null];

        const c = this.peek();
        if (c === 'Z') {
            this.consume('Z');
            zone = 'Z';
        } else if (c === '+' || c === '-') {
            const sign = c;
            this.i++;

            const zh = this.readExactDigits(2, "zone hour");
            let zm = '00';
            if (this.peek() === '.') {
                this.consume('.');
                zm = this.readExactDigits(2, "zone minute");
            }
            zone = `${sign}${zh}:${zm}`;
        }

        return [time, zone];

    }

    parseSymbolText() {

        const start = this.i;

        while (!this.eof()) {
            const c = this.peek();
            if (c === null)
                break;

            if (
                (c >= 'a' && c <= 'z')
                || (c >= 'A' && c <= 'Z')
                || (c >= '0' && c <= '9')
                || c === '_'
                || c === '-'
            ) {
                this.i++;
                continue;
            }

            break;
        }

        const text = this.s.substring(start, this.i);
        if (text === '')
            throw this.err("Expected symbol text after '!'");

        return text;

    }

    parseIdentifierText() {

        this.skipWs();
        if (!this.peekIsIdentStart())
            throw this.err("Expected identifier");

        const start = this.i;
        this.i++;

        while (!this.eof()) {
            const c = this.peek();
            if (c === null)
                break;
            if (this.isIdentPart(c)) {
                this.i++;
                continue;
            }
            break;
        }

        return this.s.substring(start, this.i);

    }

    skipWs() {
        while (!this.eof()) {
            const c = this.peek();
            if (c === ' ' || c === "\t" || c === "\r" || c === "\n") {
                this.i++;
                continue;
            }
            break;
        }
    }

    peek() {
        if (this.i >= this.n)
            return null;
        return this.s[this.i];
    }

    eof() {
        return this.i >= this.n;
    }

    consume(expected) {
        const c = this.peek();
        if (c !== expected)
            throw this.err(`Expected '${expected}', got ${c === null ? 'EOF' : `'${c}'`}`);
        this.i++;
    }

    readWhile(predicate) {
        const start = this.i;
        while (!this.eof()) {
            const c = this.peek();
            if (c === null || !predicate(c))
                break;
            this.i++;
        }
        return this.s.substring(start, this.i);
    }

    readExactDigits(count, label) {
        const start = this.i;
        for (let index = 0; index < count; index++) {
            const c = this.peek();
            if (c === null || c < '0' || c > '9')
                throw this.err(`Expected ${count} digits for ${label}`);
            this.i++;
        }
        return this.s.substring(start, start + count);
    }

    peekIsIdentStart() {
        const c = this.peek();
        return c !== null && (this.isAlpha(c) || c === '_');
    }

    isIdentPart(c) {
        return this.isAlpha(c) || (c >= '0' && c <= '9') || c === '_';
    }

    isAlpha(c) {
        return (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z');
    }

    peekIsNumberStart() {
        const c = this.peek();
        if (c === null)
            return false;
        if (c >= '0' && c <= '9')
            return true;
        if (c === '-') {
            const next = (this.i + 1 < this.n) ? this.s[this.i + 1] : null;
            return next !== null && next >= '0' && next <= '9';
        }
        return false;
    }

    err(message) {
        return new QLangParseError(message, this.i, this.s);
    }

}
